"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Block } from "@/types/block";
import {
  BLOCK_SERVER_STAMPED_EVENT,
  type BlockServerStampedDetail,
} from "@/indexedDB/stores/blocksStore";
// Direct module import (not the "@/indexedDB" barrel) so this context never
// pulls in the Dexie client and cannot form an import cycle with the barrel.
import { blockStore } from "@/indexedDB/store/blockStore";

export type UpdateOrigin = 'user' | 'remote' | 'api';

export type BlockUpdateEvent =
  | { type: 'update'; blockId: string; updates: Partial<Block> }
  | { type: 'add'; block: Block }
  | { type: 'remove'; blockId: string };

type LocalUpdateListener = (event: BlockUpdateEvent) => void;

/**
 * Global Block Context
 *
 * Stores ALL blocks in the workspace with their metadata.
 *
 * Responsibilities:
 * - Store all blocks with full metadata (Map<blockId, Block>)
 * - Maintain parent-child relationships (children index)
 * - Provide fast lookups by blockId
 * - Get children of any block
 * - Load blocks from API response
 */

interface BlockContextValue {
  // Global block storage
  blocks: Map<string, Block>;

  // Always-current blocks map — reads blocksRef.current, not React state.
  // Use this anywhere you need the map inside a setTimeout/rAF/async callback
  // to avoid stale closure snapshots.
  getBlocksMap: () => Map<string, Block>;

  // Get a single block by ID
  getBlock: (blockId: string) => Block | undefined;

  // Get children of a block (returns blockIds[])
  getChildren: (blockId: string) => string[];

  // Get all children blocks with full data
  getChildrenBlocks: (blockId: string) => Block[];

  // Update a single block
  updateBlock: (blockId: string, updates: Partial<Block>, origin?: UpdateOrigin) => void;

  // Load blocks from API response (replaces all)
  loadBlocks: (blocks: Block[]) => void;

  // Upsert blocks: Update if exists, add if not
  upsertBlocks: (blocks: Block[]) => Promise<void>;

  // Add a single block
  addBlock: (block: Block, origin?: UpdateOrigin) => void;

  // Remove a block
  removeBlock: (blockId: string, origin?: UpdateOrigin) => void;

  // Check if block exists
  hasBlock: (blockId: string) => boolean;

  // Get all blocks as array
  getAllBlocks: () => Block[];

  // Get block order for a specific page (from blockIds)
  getBlockOrder: (pageId: string) => string[];

  // Update block order for a page
  updateBlockOrder: (pageId: string, newOrder: string[]) => void;

  addLocalUpdateListener: (listener: LocalUpdateListener) => void;
  removeLocalUpdateListener: (listener: LocalUpdateListener) => void;
}

/**
 * Stable function API — everything except the reactive `blocks` map.
 * Its identity NEVER changes, so consumers of useBlockActions() do not
 * re-render when blocks change.
 */
export type BlockActionsValue = Omit<BlockContextValue, "blocks">;

// Split contexts: actions (stable identity, never triggers re-renders) vs
// data (the reactive blocks map, changes identity on every block mutation).
// Consumers that only call functions subscribe to actions; consumers that
// need to REACT to block changes subscribe to data (or use useGlobalBlocks).
const BlockActionsContext = createContext<BlockActionsValue | null>(null);
const BlockDataContext = createContext<Map<string, Block> | null>(null);

/**
 * Stable actions only. Use this when the component just calls
 * getBlock/updateBlock/etc. and does NOT need to re-render on block changes.
 */
export function useBlockActions(): BlockActionsValue {
  const ctx = useContext(BlockActionsContext);
  if (!ctx) throw new Error("useBlockActions must be used within GlobalBlockProvider");
  return ctx;
}

/**
 * Reactive blocks map. Changes identity on every block mutation — subscribe
 * only when the component must recompute/re-render as blocks change.
 */
export function useBlockData(): Map<string, Block> {
  const ctx = useContext(BlockDataContext);
  if (!ctx) throw new Error("useBlockData must be used within GlobalBlockProvider");
  return ctx;
}

/**
 * Compatibility hook: actions + reactive `blocks`, same shape as before the
 * actions/data split. Re-renders on every block change — prefer
 * useBlockActions() unless the component genuinely reads `blocks`.
 */
export function useGlobalBlocks(): BlockContextValue {
  const actions = useBlockActions();
  const blocks = useBlockData();
  return useMemo(() => ({ ...actions, blocks }), [actions, blocks]);
}

interface GlobalBlockProviderProps {
  children: React.ReactNode;
  initialBlocks?: Block[];
}

export const GlobalBlockProvider: React.FC<GlobalBlockProviderProps> = ({
  children,
  initialBlocks = []
}) => {
  // Main storage: Map<blockId, Block>
  //
  // DUAL-WRITE INVARIANT: every write path in this provider mirrors into
  // `blockStore` (the fine-grained per-entity store — see
  // indexedDB/BLOCK_STORE_REDESIGN.md). The store must always hold exactly
  // what this map holds; a missed mirror is invisible today but becomes a
  // stale-render bug once useBlock/useChildBlocks consumers ship. The write
  // paths are: updateBlock, addBlock, removeBlock, loadBlocks, upsertBlocks,
  // and the BLOCK_SERVER_STAMPED_EVENT handler below.
  const [blocksMap, setBlocksMap] = useState<Map<string, Block>>(() => {
    const map = new Map<string, Block>();
    initialBlocks.forEach(block => map.set(block._id, block));
    // Seed the mirror. Idempotent, and store notifications are deferred to a
    // microtask, so this is safe inside the state initializer (which may run
    // twice under StrictMode).
    if (initialBlocks.length > 0) blockStore.replaceAll(initialBlocks);
    return map;
  });

  const blocksRef = useRef(blocksMap);
  blocksRef.current = blocksMap;

  // Listeners for local updates
  const listenersRef = useRef(new Set<LocalUpdateListener>());

  const addLocalUpdateListener = useCallback((listener: LocalUpdateListener) => {
    listenersRef.current.add(listener);
  }, []);

  const removeLocalUpdateListener = useCallback((listener: LocalUpdateListener) => {
    listenersRef.current.delete(listener);
  }, []);

  const notifyListeners = useCallback((event: BlockUpdateEvent) => {
    listenersRef.current.forEach(listener => listener(event));
  }, []);

  // Dev-only mirror check for the dual-write invariant above. A count
  // mismatch means one of the write paths forgot its blockStore mirror call.
  // (After a hot reload the module-singleton store can briefly disagree with
  // the remounted context — a warning right after HMR is noise, one during
  // normal interaction is a real bug.)
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const storeCount = blockStore.getBlockCount();
    if (storeCount !== blocksMap.size) {
      console.warn(
        `[blockContext] blockStore mirror divergence: store has ${storeCount} block(s), ` +
        `context has ${blocksMap.size}. A provider write path is missing its blockStore call.`,
      );
    }
  }, [blocksMap]);

  // Bridge IDB → in-memory context for server-stamped values.
  //
  // When SyncWatcher finishes a /batch-create call, the server's response
  // carries the auto-assigned unique-id under `databaseProperties`. The
  // mutation layer writes that into IDB via `mergeServerStampedDatabaseProperties`
  // and fires `BLOCK_SERVER_STAMPED_EVENT`. We listen here so the
  // in-memory block reflects the stamp before any follow-up edit (e.g. an
  // instant title rename) sends the whole block back to the server — without
  // this, the next save would clobber the just-assigned number with `undefined`.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<BlockServerStampedDetail>).detail;
      if (!detail || !detail.blockId) return;

      const existing = blocksRef.current.get(detail.blockId);
      if (!existing) return;

      const prevValue =
        existing.value && typeof existing.value === "object"
          ? (existing.value as Record<string, unknown>)
          : {};
      const prevDbProps =
        "databaseProperties" in prevValue &&
        prevValue.databaseProperties &&
        typeof prevValue.databaseProperties === "object"
          ? (prevValue.databaseProperties as Record<string, unknown>)
          : {};

      const hasStampedDbProps =
        detail.databaseProperties && Object.keys(detail.databaseProperties).length > 0;
      const hasUniqueId = typeof detail.uniqueId === "number";

      const nextDbProps: Record<string, unknown> = hasStampedDbProps
        ? { ...prevDbProps, ...detail.databaseProperties }
        : { ...prevDbProps };
      if (detail.unsetPropertyId && detail.unsetPropertyId in nextDbProps) {
        delete nextDbProps[detail.unsetPropertyId];
      }

      const stampedBlock = {
        ...existing,
        value: {
          ...prevValue,
          ...(hasStampedDbProps || detail.unsetPropertyId
            ? { databaseProperties: nextDbProps }
            : {}),
          ...(hasUniqueId ? { uniqueId: detail.uniqueId } : {}),
        },
      } as Block;
      const newMap = new Map(blocksRef.current);
      newMap.set(detail.blockId, stampedBlock);
      blocksRef.current = newMap;
      setBlocksMap(newMap);
      blockStore.upsert(stampedBlock);
    };

    window.addEventListener(BLOCK_SERVER_STAMPED_EVENT, handler);
    return () => window.removeEventListener(BLOCK_SERVER_STAMPED_EVENT, handler);
  }, []);

  // Get a single block
  const getBlock = useCallback((blockId: string): Block | undefined => {
    return blocksRef.current.get(blockId);
  }, []);

  // Get children block IDs from a block's blockIds array
  const getChildren = useCallback((blockId: string): string[] => {
    const block = blocksRef.current.get(blockId);
    return block?.blockIds || [];
  }, []);

  // Get children blocks with full data
  const getChildrenBlocks = useCallback((blockId: string): Block[] => {
    const childIds = getChildren(blockId);
    return childIds
      .map(id => blocksRef.current.get(id))
      .filter((b): b is Block => b !== undefined);
  }, [getChildren]);

  // Update a block
  // BUGFIX: `origin` was previously declared in the interface but never
  // received as a parameter — making the `if (origin === 'user')` check
  // permanently false and silently dropping every local block update on
  // the floor. Listeners are what feed broadcastBlockUpdate → Y.Array,
  // so this single bug was the root cause of Yjs content not syncing
  // between browsers.
  const updateBlock = useCallback(
    (blockId: string, updates: Partial<Block>, origin: UpdateOrigin = 'user') => {
      const newMap = new Map(blocksRef.current);
      const existing = newMap.get(blockId);
      if (existing) {
        // The store takes whole blocks, so mirror the MERGED result — never
        // the partial `updates`.
        const merged = { ...existing, ...updates };
        newMap.set(blockId, merged);
        blockStore.upsert(merged);
      }
      blocksRef.current = newMap;
      setBlocksMap(newMap);

      if (origin === 'user') {
        notifyListeners({ type: 'update', blockId, updates });
      }
    },
    [notifyListeners],
  );

  // Load blocks from API (replaces all blocks)
  const loadBlocks = useCallback((blocks: Block[]) => {
    const newMap = new Map<string, Block>();
    blocks.forEach(block => newMap.set(block._id, block));
    blocksRef.current = newMap;
    setBlocksMap(newMap);
    blockStore.replaceAll(blocks);
  }, []);

  // Upsert blocks: Update if exists, add if not 
  const upsertBlocks = useCallback((blocks: Block[]): Promise<void> => {
    return new Promise((resolve) => {
      const newMap = new Map(blocksRef.current);
      blocks.forEach(block => {
        newMap.set(block._id, block);
        blockStore.upsert(block);
      });
      blocksRef.current = newMap;
      setBlocksMap(newMap);
      setTimeout(() => resolve(), 0);
    });
  }, []);

  // Add a single block
  const addBlock = useCallback((block: Block, origin: UpdateOrigin = 'user') => {
    const newMap = new Map(blocksRef.current);
    newMap.set(block._id, block);
    blocksRef.current = newMap;
    setBlocksMap(newMap);
    blockStore.upsert(block);

    if (origin === 'user') {
      notifyListeners({ type: 'add', block });
    }
  }, [notifyListeners]);

  // Remove a block
  const removeBlock = useCallback((blockId: string, origin: UpdateOrigin = 'user') => {
    const newMap = new Map(blocksRef.current);

    // Self-healing integrity: remove this block from its parent's blockIds
    const blockToRemove = newMap.get(blockId);
    if (blockToRemove && blockToRemove.parentId) {
      const parent = newMap.get(blockToRemove.parentId);
      if (parent && parent.blockIds) {
        const updatedParent = {
          ...parent,
          blockIds: parent.blockIds.filter(id => id !== blockId)
        };
        newMap.set(parent._id, updatedParent);
        // Mirror the healed parent too, or the store's child lists diverge.
        blockStore.upsert(updatedParent);
      }
    }

    newMap.delete(blockId);
    blocksRef.current = newMap;
    setBlocksMap(newMap);
    blockStore.remove(blockId);

     if (origin === 'user') {
      notifyListeners({ type: 'remove', blockId });
    }
  }, [notifyListeners]);

  // Check if block exists
  const hasBlock = useCallback((blockId: string): boolean => {
    return blocksRef.current.has(blockId);
  }, []);

  // Get all blocks as array
  const getAllBlocks = useCallback((): Block[] => {
    return Array.from(blocksRef.current.values());
  }, []);

  // Always-current blocks map — safe to call from setTimeout/rAF/async callbacks
  const getBlocksMap = useCallback((): Map<string, Block> => {
    return blocksRef.current;
  }, []);

  // LEGACY: Get block order for a specific page
  const getBlockOrder = useCallback((pageId: string): string[] => {
    const page = blocksRef.current.get(pageId);
    return page?.blockIds || [];
  }, []);

  // LEGACY: Update block order for a page
  const updateBlockOrder = useCallback((pageId: string, newOrder: string[]) => {
    // No-op guard: the editor's tracker calls this on transactions where the
    // order is UNCHANGED. Writing a fresh blockIds array anyway creates a new
    // page-block object, which notifies every subscriber of that page (e.g.
    // its sidebar rows) once per keystroke for nothing.
    const current = blocksRef.current.get(pageId)?.blockIds;
    if (
      current &&
      current.length === newOrder.length &&
      current.every((id, i) => id === newOrder[i])
    ) {
      return;
    }
    updateBlock(pageId, { blockIds: newOrder });
  }, [updateBlock]);

  // Stable actions object: every callback is useCallback-stable, so this memo
  // never recomputes in practice — its identity is constant for the provider's
  // lifetime. Consumers of useBlockActions() therefore never re-render because
  // of block changes; only BlockDataContext (blocksMap) is reactive.
  const actions: BlockActionsValue = useMemo(
    () => ({
      getBlocksMap,
      getBlock,
      getChildren,
      getChildrenBlocks,
      updateBlock,
      loadBlocks,
      upsertBlocks,
      addBlock,
      removeBlock,
      hasBlock,
      getAllBlocks,
      getBlockOrder,
      updateBlockOrder,
      addLocalUpdateListener,
      removeLocalUpdateListener,
    }),
    [
      getBlocksMap,
      getBlock,
      getChildren,
      getChildrenBlocks,
      updateBlock,
      loadBlocks,
      upsertBlocks,
      addBlock,
      removeBlock,
      hasBlock,
      getAllBlocks,
      getBlockOrder,
      updateBlockOrder,
      addLocalUpdateListener,
      removeLocalUpdateListener,
    ],
  );

  return (
    <BlockActionsContext.Provider value={actions}>
      <BlockDataContext.Provider value={blocksMap}>
        {children}
      </BlockDataContext.Provider>
    </BlockActionsContext.Provider>
  );
};
